/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import Grid from '@material-ui/core/Grid';
import withStyles from '@material-ui/core/styles/withStyles';
import { Link } from 'react-router-dom';
import {
    object, string, shape,
} from 'prop-types';
import { useUIDSeed } from 'react-uid';
import mbpLogger from 'mbp-logger';
// components
import ReactMarkdown from 'react-markdown';
import HeroBlock from '../../CustomerService/Partials/HeroBlock';
import GraphqlImoc from '../../GraphqlCategoryPage/Partials/GraphqlCatHeaderContainer/Partials/GraphqlImoc/GraphqlImoc';
import Disclaimer from '../../GraphqlCategoryPage/Partials/GraphqlDisclaimer/GraphqlDisclaimer';
import Faq from './Faq';
import isEmptyReference from '../../../../helpers/contentstack/isEmptyReference';
import TopBanner from '../../GraphqlTemplatePage/Partials/TemplateCollectionSubnavImageBanner/Partials/SubnavImageTopBanner/SubnavImageTopBanner';
import TwoColumn from '../../ConnectPage/Partials/ListBlock';
import BottomBanner from './BottomBanner';
import PromoCode from '../../PromoCodePage/PromoCodeBlock';
import SubNav from '../../GraphqlCategoryPage/Partials/GraphqlCatHeaderContainer/Partials/GraphqlSubnav/GraphqlSubnav';
import SimpleTile from './SimpleTile';
import InfoBlock from './InfoBlock';
import OurFavouritesBlock from './OurFavouritesBlock';
import ArticleGiftGuide from '../../GraphqlTemplatePage/Partials/TemplateCollectionGiftGuides/ArticleGiftGuide';
import MainHero from './MainHero';
import BestCollectionData from './BestCollectionData';
import MainBanner from './MainBanner';
import NupSimple from '../../../ContentTypeComponents/NupSimple/NupSimple';
import GraphqlIcBanner from '../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlIcBanner';
import BannerCategory from '../../GraphqlTemplatePage/Partials/TemplateCollectionGiftGuides/BannerCategory';
import CategoryGiftGuides from '../../GraphqlTemplatePage/Partials/TemplateCollectionGiftGuides/CategoryGiftGuides';
import DesktopMobile from '../../../../helpers/DesktopMobile/DesktopMobile';
import DesktopGiftBlock from '../../GraphqlTemplatePage/Partials/TemplateCollectionSem/GraphqlGiftPage/DesktopGiftBlock/DesktopGiftBlock';
import MobileGiftBlock from '../../GraphqlTemplatePage/Partials/TemplateCollectionSem/GraphqlGiftPage/MobileGiftBlock/MobileGiftBlock';
import SubscriptionBlock from './SubscriptionBlock';
import GraphqlSaleEntry from './GraphqlSaleEntry';

const styles = (theme) => ({
    subCollection: {
        '& h2, & h4, & a': {
            textAlign: 'center',
            width: '100%',
            margin: '7px 0',
        },
        '& h4': {
            fontWeight: 'normal',
        },
        '& a': {
            fontSize: 16,
            fontWeight: 'bold',
            color: theme.palette.colorPrimary,
        },
    },
    subBanner: {
        maxWidth: '80%',
        margin: 'auto',
        padding: '30px 0',
        '& img': {
            width: '100%',
        },
    },
    imocStyle: {
        '& div': {
            padding: '10px',
            margin: '0',
        },
    },
    subNav: {
        flex: '0 1 auto',
        display: 'flex',
        [theme.breakpoints.down('xs')]: {
            display: 'block',
        },
        '& a': {
            flexGrow: 1,
            fontSize: 12,
            maxWidth: '100%',
            flexBasis: '100%',
            textAlign: 'center',
            textTransform: 'uppercase',
            textDecoration: 'none',
            padding: '10px 0',
            [theme.breakpoints.down('xs')]: {
                display: 'block',
                marginBottom: 3,
            },
        },
    },
    line: {
        width: '100%',
        height: 1,
        backgroundColor: theme.palette.grey8,
        marginTop: 20,
        border: 0,
    },
    brandBlock: {
        width: '80%',
        display: 'flex',
        margin: ' 0 auto',
        justifyContent: 'center',
        [theme.breakpoints.down('xs')]: {
            width: '90%',
        },
    },
    brandImage3row: {
        '& img': {
            width: '57%',
        },
    },
    brandImage4row: {
        '& img': {
            width: '100%',
        },
    },
    brandHeading: {
        textAlign: 'center',
        width: '100%',
        fontSize: 26,
        padding: '40px 0',
        [theme.breakpoints.down('xs')]: {
            padding: '20px 0',
            fontSize: 20,
        },
    },
    featureBlock: {
        justifyContent: 'center',
        alignItems: 'flex-end',
        '& img': {
            width: '100%',
        },
    },
    featureHeading: {
        padding: '15px 0',
        fontSize: 25,
        textAlign: 'center',
        width: '100%',
        fontWeight: 'bold',
        overflow: 'hidden',
        margin: '20px 0 25px',
        [theme.breakpoints.down('xs')]: {
            fontSize: 20,
        },
        '&::before,&::after': {
            width: '72%',
            height: 1,
            content: '""',
            display: 'inline-block',
            position: 'relative',
            verticalAlign: 'middle',
            backgroundColor: theme.palette.grey8,
            [theme.breakpoints.down('xs')]: {
                display: 'none',
            },
        },
        '&::after': {
            marginRight: '-50%',
            left: '.5em',
        },
        '&::before': {
            marginLeft: '-50%',
            right: '0.5em',
        },
    },
    banner: {
        '& img': {
            width: '100%',
            display: 'block',
            [theme.breakpoints.down(600)]: {
                marginBottom: 10,
            },
        },
    },
    bannerText: {
        textAlign: 'center',
        fontSize: 30,
        lineHeight: 1.2,
        [theme.breakpoints.down(600)]: {
            fontSize: 20,
            padding: '0 10px',
        },
    },
    bottomContainer: {
        width: '60%',
        margin: 'auto',
        paddingBottom: '25px',
    },
});

const MasterTemplatePageBlock = ({
    classes, block, ssrDeviceType, brand,
}) => {
    if (!block) {
        mbpLogger.logWarning({
            message: 'No block types have been passed into MasterTemplatePageBlock.js',
        });
        return null;
    }
    const blockKey = Object.keys(block)[0];
    const blockObj = block[blockKey];
    const seed = useUIDSeed();
    if (isEmptyReference(blockObj, blockKey, 'MasterMasterTemplatePageBlock.js')) return null;

    const fontFamily = (data) => data?.replace(/font-family: |;/g, '');

    switch (blockKey) {
        case 'header':
        case 'banners':
        case 'bottom':
        {
            return (
                <>
                    <DesktopMobile
                        desktop={() => <DesktopGiftBlock block={block} />}
                        mobile={() => <MobileGiftBlock block={block} />}
                    />
                </>
            );
        }

        case 'subscription_block':
            return <SubscriptionBlock item={block} />;

        case 'sale_entry':
            return  <GraphqlSaleEntry brand={brand} collectionCode={block?.sale_entry?.code} blockData={block} />;

        case 'imoc':
            return (
                <div className={classes.imocStyle}>
                    <GraphqlImoc brand={brand} imocBlock={blockObj} />
                </div>
            );

        case 'in_collection_banners':
            return <GraphqlIcBanner icBannerBlock={blockObj} presentation_family={fontFamily} />;

        case 'disclaimer':
            return <Disclaimer disclaimerBlock={blockObj} />;

        case 'faq':
            return <Faq faqBlock={blockObj} />;

        case 'nup_simple':
            return <NupSimple data={blockObj} />;

        case 'main_hero':
            return <MainHero key={seed(blockObj)} data={blockObj} fontFamily={fontFamily} />;

        case 'sub_nav':
        {
            return   (
                <Grid key={seed(blockObj)} item xs={12}>
                    <div className={classes.subNav}>
                        {
                    blockObj?.link.length > 0
                   && blockObj.link.map((list) => (
                       <Link key={seed(list)} style={{ color: `${blockObj?.font_color}`,  backgroundColor: `${blockObj?.background_color}` }} to={list?.href}>{list?.title}</Link>
                   ))
                        }
                    </div>
                </Grid>
            );
        }

        case 'banner':
            // Top Banner Text
            if (blockObj?.desktop_image === null) {
                return (
                    <Grid key={seed(blockObj)} item xs={12} className={classes.bannerText}>
                        <ReactMarkdown source={blockObj?.main_copy} />
                    </Grid>
                );
            }

            // Bottom Banner
            if (blockObj?.link?.href) {
                const newHref = blockObj.link.href.includes('/blog') ? `https://www.${brand?.domain}.com${blockObj.link.href}`
                    : blockObj.link.href;
                const {  url: desktopUrl = '', filename = '' } = blockObj?.desktop_image || {};
                const { url: mobileUrl = '', filename: mobileFilename = '' } = blockObj?.mobile_image || {};
                return (
                    <Grid key={seed(blockObj)} className={classes.banner} item xs={12}>
                        <Link to={newHref}>
                            <DesktopMobile
                                mobile={() =>  <img src={mobileUrl} alt={mobileFilename} />}
                                desktop={() =>  <img src={desktopUrl} alt={filename} />}
                            />
                        </Link>
                    </Grid>
                );
            }
            return null;

        case 'articles':
            return <ArticleGiftGuide article={blockObj?.article} articlesPerRow={blockObj?.article_per_row} />;

        case 'best_sellers':
            return <BestCollectionData blockObj={blockObj} />;

        case 'main_banner':
            return <MainBanner item={blockObj} />;

        case 'sub_banner':
        {
            const sub_banner = blockObj;
            return (
                <Grid key={seed(blockObj)} item xs={12} className={classes.subBanner}>
                    <img src={sub_banner?.desktop_image?.url} alt={sub_banner?.desktop_image?.title}  />
                </Grid>
            );
        }

        case 'bottom_banners':
        {
            const bottom_banners = blockObj;
            return (
                <Grid key={seed(blockObj)} container className={classes.bottomBanner}>
                    <Grid container><h2>{bottom_banners?.title}</h2>
                    </Grid>
                    {
                        bottom_banners?.banners_block?.map((data) => {
                            const href = data?.link?.href || '';
                            const url = data?.desktop_image?.url || '';
                            const title = data?.desktop_image?.title || '';
                            return (
                                <Grid key={seed(data)} item xs={6}>
                                    <div className={classes.bottomBannerInner}>
                                        {href !== ''
                                            ?  <a href={href}><img src={url} alt={title} /></a>
                                            : <img src={url} alt={title} />}
                                    </div>
                                </Grid>
                            );
                        })
                    }
                </Grid>
            );
        }

        case 'categories':
        {
            const { heading = '',  categories_per_row } = blockObj;

            if (categories_per_row === 1) {
                return (
                    <Grid key={seed(blockObj)} container>
                        <BannerCategory categories={blockObj} classes={classes}  />
                        <hr className={classes.line} />
                    </Grid>
                );
            }
            if (heading?.includes('Brands')) {
                return (
                    <Grid key={seed(blockObj)} container className={`${classes.brandBlock} ${categories_per_row === 3 ? classes.brandImage3row : classes.brandImage4row}`}>
                        <div className={classes.brandHeading}>{heading}</div>
                        {blockObj ? <CategoryGiftGuides categories={blockObj} classes={classes}  /> : null}
                    </Grid>
                );
            }
            if (heading?.includes('Love Most') || heading?.includes('SHOP')) {
                return (
                    <Grid key={seed(blockObj)} container className={classes.featureBlock}>
                        {heading !== '' ?   <div className={classes.featureHeading}>{heading}</div> : '' }
                        {blockObj ? <CategoryGiftGuides categories={blockObj} classes={classes} /> : null }
                        <hr className={classes.line} />
                    </Grid>
                );
            }
            return  (
                <Grid key={seed(blockObj)} container className={classes.categoryBlock}>
                    {heading !== ''
                                && <div className={classes.brandHeading}>{heading}</div>}
                    <CategoryGiftGuides categories={blockObj} classes={classes}  />
                    <hr className={classes.line} />
                </Grid>
            );
        }

        case 'top_banner':
            return <TopBanner topBanner={blockObj} mobileBannerData={blockObj} />;

        case 'sub_navigation':
            return <SubNav key={blockKey} subnavBlock={blockObj} />;

        case 'two_column':
            return <TwoColumn key={seed(blockObj)} data={blockObj} brand={brand} />;

        case 'hero':
            return <HeroBlock data={blockObj} showTitle ssrDeviceType={ssrDeviceType} />;

        case 'bottom_banner':
            return <BottomBanner classes={{}} bottomBanner={blockObj} />;

        case 'promo_card':
            return <PromoCode block={blockObj} />;

        case 'simple_tile_row':
            return <SimpleTile data={blockObj} fontFamily={fontFamily} />;

        case 'info_block':
            return <InfoBlock infoBlockData={blockObj} />;

        case 'our_favourites_block':
            return <OurFavouritesBlock ourFavoritesBlockData={blockObj} />;

        default:
            mbpLogger.logWarning({
                message: `Unknown HomePage block type "${blockKey}" in MasterTemplatePageBlock.js`,
            });
            return null;
    }
};

MasterTemplatePageBlock.propTypes = {
    classes: object.isRequired,
    block: object.isRequired,
    brand: shape({
        code: string,
    }).isRequired,
    ssrDeviceType: string.isRequired,
};

export default withStyles(styles)(MasterTemplatePageBlock);
